import appSettings from "../../settings/appSettings";

const { widgetId="" } = appSettings;

/**
 * Retrieves the registration type parameter from the URL query string.
 *
 * This function checks the URL for a query parameter named "regtype".
 * If the parameter exists, it returns its value.
 * If the parameter does not exist, it returns the default value of `widgetId`.
 *
 * @returns {string} The value of the "regtype" query parameter if it exists,
 * otherwise returns the default value of `widgetId`.
 */
export const getRegTypeParam = (defaultVal = "") => new URLSearchParams(window.location.search).get("regtype") || defaultVal;

/**
 * Triggers a custom analytics event with the specified name and data by dispatching the event
 * which should have a corresponding listener defined elsewhere.
 *
 * @param {string} eventName - The name of the event to trigger.
 * @param {Object} [eventData={}] - Optional data to include with the event.
 */
export const triggerAnalyticsEvent = (eventName, eventData = {}) => {
  if (!eventName) {
    console.error("triggerAnalyticsEvent: eventName is required");
    return;
  }

  const event = new CustomEvent(eventName, {
    detail: eventData
  });

  document.dispatchEvent(event);
};

/**
 * Converts the given event data to a JSON string.
 *
 * @param {Object} eventData - The event data to be converted.
 * @returns {string} The JSON string representation of the event data, or an empty string if conversion fails.
 */
const getEventDataKey = (eventData) => {
  try {
    return JSON.stringify(eventData);
  } catch (error) {
    return "";
  }
};

/**
 * A Map to store event data keys.
 * This map is used to manage and retrieve event data keys for analytics purposes.
 * Needs to live outside of the function to persist across function calls.
 */
export const eventDataMapKeys = new Map();

/**
 * Triggers an event with the provided event data only once.
 *
 * This function maintains a map of event data keys to ensure that the event
 * is triggered only once for each unique event data. If the event data key
 * has not been encountered before, it triggers the event using the provided
 * event trigger function.
 *
 * @param {Object} eventData - The data associated with the event.
 * @param {Function} eventTriggerFunc - The function to call to trigger the event.
 */
const triggerEventDataOnce = (eventData, eventTriggerFunc) => {
  const eventDataKey = getEventDataKey(eventData);

  if (eventDataKey && !eventDataMapKeys.has(eventDataKey)) {
    eventDataMapKeys.set(eventDataKey, eventData);
    eventTriggerFunc(eventData);
  }
};

/**
 * Triggers the community registration view analytics event.
 *
 * @param {Object} [eventData={}] - The data to be sent with the event.
 * @param {boolean} [triggerOnce=true] - If true, the event will only be triggered once.
 */
export const triggerCommunityRegistrationView = (eventData = {}, triggerOnce = true) => {
  if (triggerOnce) {
    triggerEventDataOnce(eventData, () =>
      triggerAnalyticsEvent("bettermode_community_registration", {
      ...eventData,
      regType: getRegTypeParam(widgetId)
      })
    );
  } else {
    triggerAnalyticsEvent("bettermode_community_registration", {
      ...eventData,
      regType: getRegTypeParam(widgetId)
    });
  }
};

/**
 * Triggers an analytics event for an error view.
 *
 * @param {Object} [eventData={}] - Additional data to include with the event.
 */
export const triggerErrorView = (eventData = {}) => {
  triggerAnalyticsEvent("error_view", {
    ...eventData
  });
};

/**
 * Tracks an internal link click event and triggers an analytics event.
 *
 * @param {Object} [eventData={}] - The event data to be sent with the analytics event.
 * @param {string} [eventData.event_label] - The label for the event.
 */
const linkTracking = (eventData = {}) => {
  triggerAnalyticsEvent("internal_link_dl", {
    ...eventData,
    link_data_info: eventData.event_label,
    link_data_target: "internal_link"
  });
};

/**
 * Handles link tracking for internal links based on the provided key.
 *
 * @param {string} key - The key indicating the type of link interaction.
 *                       Expected values are "back" or "next".
 *
 * @example
 * handleLinkTracking("back");
 * handleLinkTracking("next");
 */
export const handleLinkTracking = (key) => {
  if (key === "back") {
    linkTracking({
      event_action: "text_click",
      event_category: "internal_link",
      event_label: `${key}:${window.location.href}`,
      link_data_type: "text"
    });
  }

  if (key === "next") {
    linkTracking({
      event_action: "button_click",
      event_category: "internal_link",
      event_label: `${key}:${window.location.href}`,
      link_data_type: "button"
    });
  }
};
